import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Save, CalendarClock, Plus, X } from "lucide-react";

const GIORNI = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Gio", 3], ["Ven", 4], ["Sab", 5], ["Dom", 6]];

export default function Appointments() {
  const { user } = useAuth();
  const isAdmin = ["admin", "team_leader"].includes(user?.role);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/appointments").then((r) => setCfg(r.data)); }, []);

  const toggleDay = (d) => setCfg((c) => ({ ...c, days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d].sort() }));
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const bandsOf = (c, d) => (c.day_hours?.[String(d)]?.length ? c.day_hours[String(d)] : [{ start: c.start, end: c.end }]);
  const setBands = (d, bands) => setCfg((c) => ({ ...c, day_hours: { ...(c.day_hours || {}), [String(d)]: bands } }));
  const editBand = (d, i, k, v) => setBands(d, bandsOf(cfg, d).map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const addBand = (d) => { const bs = bandsOf(cfg, d); const last = bs[bs.length - 1]; setBands(d, [...bs, { start: last?.end || "09:00", end: last?.end ? `${String(Math.min(23, Number(last.end.slice(0, 2)) + 2)).padStart(2, "0")}:${last.end.slice(3)}` : "12:00" }]); };
  const delBand = (d, i) => setBands(d, bandsOf(cfg, d).filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/appointments", {
        days: cfg.days, start: cfg.start, end: cfg.end, slot_min: Number(cfg.slot_min), facility: cfg.facility,
        day_hours: Object.fromEntries(cfg.days.map((d) => [String(d), bandsOf(cfg, d)])),
      });
      setCfg(data); toast.success("Disponibilità salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Appuntamenti</h1>
          <p className="text-xs text-muted-foreground">Disponibilità per le consulenze (online o in visita)</p>
        </div>
        {isAdmin && cfg && (
          <Button onClick={save} disabled={saving} data-testid="save-appointments-btn" className="font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva
          </Button>
        )}
      </header>

      <div className="p-4 lg:p-8">
        {!cfg ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <Card className="max-w-xl rounded-lg border-border p-6 space-y-5" data-testid="appointments-card">
            <div className="flex items-center gap-2 text-sm font-semibold"><CalendarClock className="h-4 w-4 text-primary" /> Finestra di disponibilità</div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Giorni</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {GIORNI.map(([l, d]) => (
                  <button key={d} disabled={!isAdmin} onClick={() => toggleDay(d)} data-testid={`day-${d}`}
                    className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${cfg.days.includes(d) ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Fasce orarie per giorno</Label>
              <div className="mt-1.5 space-y-2" data-testid="appt-day-hours">
                {cfg.days.length === 0 && <p className="text-xs text-muted-foreground">Seleziona almeno un giorno.</p>}
                {GIORNI.filter(([, d]) => cfg.days.includes(d)).map(([l, d]) => (
                  <div key={d} className="flex items-start gap-3 rounded-md border border-border p-2.5" data-testid={`appt-day-row-${d}`}>
                    <span className="w-10 pt-2 text-sm font-semibold text-primary">{l}</span>
                    <div className="flex-1 space-y-1.5">
                      {bandsOf(cfg, d).map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input type="time" value={b.start} disabled={!isAdmin} onChange={(e) => editBand(d, i, "start", e.target.value)} className="h-9 w-28" data-testid={`appt-band-${d}-${i}-start`} />
                          <span className="text-muted-foreground text-xs">–</span>
                          <Input type="time" value={b.end} disabled={!isAdmin} onChange={(e) => editBand(d, i, "end", e.target.value)} className="h-9 w-28" data-testid={`appt-band-${d}-${i}-end`} />
                          {b.start >= b.end && <span className="text-[11px] text-red-400">fine prima dell'inizio</span>}
                          {isAdmin && bandsOf(cfg, d).length > 1 && (
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => delBand(d, i)} data-testid={`appt-band-${d}-${i}-del`}><X className="h-4 w-4" /></Button>
                          )}
                        </div>
                      ))}
                      {isAdmin && (
                        <button type="button" onClick={() => addBand(d)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid={`appt-band-${d}-add`}><Plus className="h-3 w-3" />Aggiungi fascia</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Durata slot (min)</Label><Input type="number" value={cfg.slot_min} disabled={!isAdmin} onChange={(e) => set("slot_min", e.target.value)} data-testid="appt-slot" /></div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Sede (per visite in stabilimento)</Label>
              <Input value={cfg.facility} disabled={!isAdmin} onChange={(e) => set("facility", e.target.value)} data-testid="appt-facility" />
            </div>
            {!isAdmin && <p className="text-xs text-amber-600">Solo gli amministratori possono modificare la disponibilità.</p>}
          </Card>
        )}
      </div>
    </div>
  );
}
